import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Underline } from '@tiptap/extension-underline';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import {
  Bold,
  Check,
  Italic,
  Link2,
  List,
  ListOrdered,
  Underline as UnderlineIcon,
  X
} from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

export interface RichTextEditorHandle {
  insertContent: (text: string) => void;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

function ToolbarButton({
  onClick,
  isActive,
  disabled,
  children
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}): React.ReactNode {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-7 w-7 text-muted-foreground hover:text-foreground',
        isActive && 'bg-accent text-foreground'
      )}
    >
      {children}
    </Button>
  );
}

function Toolbar({
  editor,
  disabled
}: {
  editor: Editor | null;
  disabled?: boolean;
}): React.ReactNode {
  const [linkInputOpen, setLinkInputOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  if (!editor) return null;

  function openLinkInput(): void {
    const prev = editor?.getAttributes('link').href as string | undefined;
    setLinkUrl(prev ?? '');
    setLinkInputOpen(true);
  }

  function applyLink(): void {
    setLinkInputOpen(false);
    if (linkUrl === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor?.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    }
  }

  function cancelLink(): void {
    setLinkInputOpen(false);
    editor?.commands.focus();
  }

  if (linkInputOpen) {
    return (
      <div className="flex items-center gap-1 border-b border-input bg-muted/40 p-1">
        <input
          autoFocus
          type="url"
          value={linkUrl}
          onChange={(e) => {
            setLinkUrl(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              applyLink();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelLink();
            }
          }}
          placeholder="https://"
          className="h-7 min-w-0 flex-1 rounded border border-input bg-background px-2 text-xs outline-none focus:border-ring focus:ring-1 focus:ring-ring"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={applyLink}
        >
          <Check className="size-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={cancelLink}
        >
          <X className="size-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5 border-b border-input bg-muted/40 p-1">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        disabled={disabled}
      >
        <Bold className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        disabled={disabled}
      >
        <Italic className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        disabled={disabled}
      >
        <UnderlineIcon className="size-3.5" />
      </ToolbarButton>
      <div className="mx-1 h-4 w-px bg-border" />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        disabled={disabled}
      >
        <List className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        disabled={disabled}
      >
        <ListOrdered className="size-3.5" />
      </ToolbarButton>
      <div className="mx-1 h-4 w-px bg-border" />
      <ToolbarButton onClick={openLinkInput} isActive={editor.isActive('link')} disabled={disabled}>
        <Link2 className="size-3.5" />
      </ToolbarButton>
    </div>
  );
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(function RichTextEditor(
  { value, onChange, disabled, placeholder },
  ref
) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        strike: false
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder })
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    }
  });

  // Sync value from parent (e.g. after reset/save) without resetting cursor on every keystroke
  useEffect(() => {
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  // Sync editable state
  useEffect(() => {
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useImperativeHandle(ref, () => ({
    insertContent: (text: string) => {
      editor.chain().focus().insertContent(text).run();
    }
  }));

  return (
    <div
      className={cn(
        'rich-text-editor rounded-lg border border-input bg-transparent dark:bg-input/30 overflow-hidden',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0',
        disabled && 'opacity-50 pointer-events-none'
      )}
    >
      <Toolbar editor={editor} disabled={disabled} />
      <EditorContent editor={editor} />
    </div>
  );
});
